import { Component, ElementRef, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivitiContentService, ContentWidgetComponent, FormBaseModule, PipeModule } from '@alfresco/adf-core';
import { fromPromise } from 'rxjs/internal-compatibility';
import { saveAs } from 'file-saver';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogActions, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ApsDocumentLinkElementPreviewService } from './aps-document-link-element-preview.service';
import { map, shareReplay, take } from 'rxjs/operators';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RelatedContentRepresentation } from '@alfresco/js-api';
import { Observable, Subscription } from 'rxjs';
import { DestroyableComponent } from '../destroyable/destroyable.component';

@Component({
  selector: 'shared-ui-aps-document-link-element',
  standalone: true,
  imports: [CommonModule, PipeModule, FormBaseModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, MatDialogModule, TranslateModule, PdfViewerModule],
  templateUrl: './aps-document-link-element.component.html',
  styleUrls: ['./aps-document-link-element.component.scss'],
  providers: [ApsDocumentLinkElementPreviewService],
  animations: [
    trigger('fadeInOut', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', animate('100ms ease-out')),
      transition('hidden => visible', animate('100ms ease-in'))
  ])]
})
export class ApsDocumentLinkElementComponent extends DestroyableComponent {

  readonly elementRef = inject(ElementRef);
  readonly dialog = inject(MatDialog);
  readonly domSanitizer = inject(DomSanitizer);
  readonly activitiContentService = inject(ActivitiContentService);
  readonly previewService = inject(ApsDocumentLinkElementPreviewService);

  @Input()
  public document: RelatedContentRepresentation;

  @Input()
  public showDocumentContent: boolean = false;

  @Input()
  public dataName: string;

  private dialogRef: MatDialogRef<MatDialogActions>;
  @ViewChild(ContentWidgetComponent) contentWidget: ContentWidgetComponent;
  @ViewChild('dialogTemplate') dialogTemplate: TemplateRef<MatDialogActions>;

  private previewSubscription?: Subscription;
  public previewBlobUrl$: Observable<string | null>;
  public isPreviewLoading: boolean = false;
  public previewBlobUrl: string | null = null;

  public thumbnailBlobUrl$: Observable<SafeUrl | null>;

  constructor() {
    super();
    this.destroyed$.subscribe(() => {
      this.stopPreviewLoading();
    })
  }

  ngOnInit(): void {
    this.thumbnailBlobUrl$ = this.previewService.getRenditionBlobUrl(this.document, 'thumbnail', 5).pipe(
      map((url) => this.domSanitizer.bypassSecurityTrustUrl(url)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.loadPreviewBlobUrl();
  }

  public downloadContent($event: MouseEvent, contentId: number, filename: string) {
    $event.preventDefault();
    fromPromise(this.activitiContentService.contentApi.getRawContent(contentId))
      .subscribe((content: string) => {
        saveAs(content, filename);
      })
  }

  public getContentUrl(contentId: number): string {
    return this.activitiContentService.contentApi.getRawContentUrl(contentId);
  }

  public openViewer() {
    const options: MatDialogConfig = {
      position: {
        top: 10 + '%',
        left: 10 + '%'
      },
      height: '100%',
      width: '100%',
      hasBackdrop: true,
      disableClose: false,
      panelClass: 'dark-panel-dialog',
      backdropClass: 'dark-backdrop-dialog'
    };
    this.dialogRef = this.dialog.open(this.dialogTemplate, options);
    this.dialogRef.afterClosed().subscribe(() => {
      this.stopPreviewLoading();
      this.dialogRef = null;
    });
  }

  public closeViewer() {
    this.dialogRef.close();
  }

  private loadPreviewBlobUrl(): void {
    if (this.previewBlobUrl || this.isPreviewLoading) {
      return;
    }

    this.initPreviewBlobUrl();
    this.isPreviewLoading = true;

    this.previewSubscription?.unsubscribe();
    this.previewSubscription = this.previewBlobUrl$
      .pipe(take(1))
      .subscribe((url) => {
        this.previewBlobUrl = url;
        this.isPreviewLoading = false;
      });
  }

  private initPreviewBlobUrl(): void {
    if (this.previewBlobUrl$) {
      return;
    }
    this.previewBlobUrl$ = this.previewService.getRenditionBlobUrl(this.document, 'preview').pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private stopPreviewLoading(): void {
    this.previewSubscription?.unsubscribe();
    this.previewSubscription = undefined;
    this.isPreviewLoading = false;
  }
}
